import { formatTimeFromSeconds } from "utils/timeUtils";
import { HistoryActors, withActorSuffix } from "./historyActors";
import {
  formatLegacyRequest,
  sanitizeOrPassthrough,
} from "./formatLegacyRequest";
import {
  extractPayloadFromRequest,
  formatPayloadSummary,
} from "./formatPayloadSummary";
import {
  HISTORY_FIELD_META,
  formatScalarValue,
  humanizeFieldKey,
} from "./historyFieldLabels";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface ScheduleLike {
  on?: string | number;
  off?: string | number;
  onTime?: string;
  offTime?: string;
  days?: string | boolean[] | number;
  speed?: string | number;
}

interface HistoryChange {
  field?: string;
  label?: string;
  oldValue?: unknown;
  newValue?: unknown;
  from?: unknown;
  to?: unknown;
}

function toDayBits(days: ScheduleLike["days"]): boolean[] {
  if (Array.isArray(days)) return days.slice(0, 7).map(Boolean);
  if (typeof days === "string") {
    return days
      .slice(0, 7)
      .split("")
      .map((bit) => bit === "1");
  }
  return [];
}

function formatDays(days: ScheduleLike["days"]): string {
  const bits = toDayBits(days);
  if (bits.length === 0 || bits.every((bit) => !bit)) return "No days";
  if (bits.length >= 7 && bits.slice(0, 7).every(Boolean)) return "Every day";
  return bits
    .map((bit, index) => (bit ? DAY_LABELS[index] : null))
    .filter(Boolean)
    .join("/");
}

function formatClock(value: string | number | undefined): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "string" && value.includes(":")) {
    return value.slice(0, 5);
  }
  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return formatTimeFromSeconds(seconds, "24h");
  }
  return "—";
}

function formatOneSchedule(schedule: ScheduleLike): string {
  const on = formatClock(schedule.onTime ?? schedule.on);
  const off = formatClock(schedule.offTime ?? schedule.off);
  const speed = schedule.speed ?? "—";
  return `${formatDays(schedule.days)} · ${on}–${off} · speed ${speed}`;
}

function formatSchedulesChange(oldValue: unknown, newValue: unknown): string {
  const oldList = Array.isArray(oldValue) ? oldValue : [];
  const newList = Array.isArray(newValue) ? newValue : [];
  const count = Math.max(oldList.length, newList.length, 0);
  if (count === 0) return "Schedules updated";

  const lines: string[] = [];
  let anyChanged = false;
  for (let i = 0; i < count; i++) {
    const before = oldList[i]
      ? formatOneSchedule(oldList[i] as ScheduleLike)
      : null;
    const after = newList[i]
      ? formatOneSchedule(newList[i] as ScheduleLike)
      : null;
    if (before && after && before === after) {
      lines.push(`Timer ${i + 1}: ${after}`);
      continue;
    }
    anyChanged = true;
    if (before && after) lines.push(`Timer ${i + 1}: ${before}\n  → ${after}`);
    else if (after) lines.push(`Timer ${i + 1}: ${after}`);
    else if (before) lines.push(`Timer ${i + 1}: removed (${before})`);
  }
  return [anyChanged ? "Schedules updated" : "Schedules", ...lines].join("\n");
}

function resolveChangeValues(change: HistoryChange) {
  return {
    oldValue: change.oldValue !== undefined ? change.oldValue : change.from,
    newValue: change.newValue !== undefined ? change.newValue : change.to,
  };
}

/** Rows about one dealer's access to the device (link or temporary access). */
const DEALER_ACCESS_FIELDS = new Set(["dealerLink", "temporaryDealerAccess"]);

function formatDealerAccessStatus(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  return String(value).replace(/_/g, " ");
}

/**
 * "Temporary dealer access for CF Pool Services: active",
 * "Dealer link for CF Pool Services: accepted → removed". A missing previous
 * status is not shown as "cleared" (these rows never record one).
 */
function formatDealerAccessChange(
  label: string,
  oldValue: unknown,
  newValue: unknown,
  dealerName?: string | null,
): string {
  const subject = dealerName ? `${label} for ${dealerName}` : label;
  const from = formatDealerAccessStatus(oldValue);
  const to = formatDealerAccessStatus(newValue) ?? "removed";
  return from ? `${subject}: ${from} → ${to}` : `${subject}: ${to}`;
}

function formatChange(
  change: HistoryChange,
  options: HistoryDetailsOptions = {},
): string {
  const { dealerName, ...actors } = options;
  const field = change.field || "";
  const label =
    change.label ||
    HISTORY_FIELD_META[field]?.label ||
    (field ? humanizeFieldKey(field) : "Change");
  const { oldValue, newValue } = resolveChangeValues(change);

  if (field === "schedules") return formatSchedulesChange(oldValue, newValue);

  if (field === "userLink") {
    const oldLabel =
      oldValue == null ? "—" : actors?.userDisplayName || String(oldValue);
    const newLabel =
      newValue == null ? "—" : actors?.userDisplayName || String(newValue);
    return withActorSuffix(`${label}: ${oldLabel} → ${newLabel}`, actors);
  }

  if (DEALER_ACCESS_FIELDS.has(field)) {
    return formatDealerAccessChange(label, oldValue, newValue, dealerName);
  }

  const oldVal = Array.isArray(oldValue)
    ? oldValue.join(", ")
    : formatScalarValue(field, oldValue) ?? String(oldValue ?? "—");
  const newVal = Array.isArray(newValue)
    ? newValue.join(", ")
    : formatScalarValue(field, newValue) ?? String(newValue ?? "—");
  return `${label}: ${oldVal} → ${newVal}`;
}

function formatFromPayloadOrRequest(
  event: { request?: string; payload?: unknown },
  actors?: HistoryActors,
): string {
  if (
    event.payload &&
    typeof event.payload === "object" &&
    !Array.isArray(event.payload)
  ) {
    const summary = formatPayloadSummary(
      event.payload as Record<string, unknown>,
    );
    if (summary) return withActorSuffix(summary, actors);
  }

  const request = event.request || "";
  if (!request) return "—";

  const legacy = formatLegacyRequest(request, actors);
  if (legacy) return legacy;

  const embedded = extractPayloadFromRequest(request);
  if (embedded) {
    const summary = formatPayloadSummary(embedded);
    if (summary) return withActorSuffix(summary, actors);
  }

  return sanitizeOrPassthrough(request);
}

/** Display names resolved by the API (user/actor) plus the dealer on access rows. */
interface HistoryDetailsOptions extends HistoryActors {
  /** Dealer behind a dealer-link / temporary-access row (from `row.dealer`). */
  dealerName?: string | null;
}

/** Prefer structured changes so DB/shadow schedule shapes both display correctly. */
export function formatHistoryDetails(
  event: {
    request?: string;
    payload?: unknown;
    changes?: HistoryChange[];
  },
  options: HistoryDetailsOptions = {},
): string {
  if (Array.isArray(event.changes) && event.changes.length > 0) {
    return event.changes
      .map((change) => formatChange(change, options))
      .join("\n");
  }
  return formatFromPayloadOrRequest(event, {
    userDisplayName: options.userDisplayName,
    actorDisplayName: options.actorDisplayName,
  });
}
